import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Settings, DollarSign, ToggleLeft, ToggleRight, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/settings`, { headers: getAuthHeaders() });
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
      setLoading(false);
    };
    fetchSettings();
  }, [getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform-admin/settings`, settings, { headers: getAuthHeaders() });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const toggleFeature = (feature) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const updatePricing = (key, value) => {
    setSettings(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [key]: parseFloat(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Platform Settings</h1>
          <p className="text-muted-foreground">Configure pricing, features, and platform behavior</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-black">
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Warning */}
      <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-sm flex items-start gap-3">
        <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-amber-400">Caution</p>
          <p className="text-sm text-muted-foreground">
            Changes to pricing will only affect new subscriptions. Existing subscriptions will remain at their current rates.
          </p>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <DollarSign className="text-primary" size={20} />
          <h2 className="font-heading text-xl uppercase">Pricing Configuration</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Coach Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground">Coach Plans</h3>
            <div>
              <label className="text-sm text-muted-foreground">Standard Plan ($/month)</label>
              <Input
                type="number"
                step="0.01"
                value={settings?.pricing?.coach_standard || 0}
                onChange={(e) => updatePricing('coach_standard', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Pro Plan ($/month)</label>
              <Input
                type="number"
                step="0.01"
                value={settings?.pricing?.coach_pro || 0}
                onChange={(e) => updatePricing('coach_pro', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Athlete Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground">Athlete Passes</h3>
            <div>
              <label className="text-sm text-muted-foreground">Athlete Pass Base ($/month)</label>
              <Input
                type="number"
                step="0.01"
                value={settings?.pricing?.athlete_pass || 0}
                onChange={(e) => updatePricing('athlete_pass', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Athlete Plus Add-on ($/month)</label>
              <Input
                type="number"
                step="0.01"
                value={settings?.pricing?.athlete_plus_addon || 0}
                onChange={(e) => updatePricing('athlete_plus_addon', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Athlete Plus Direct Entry ($/month)</label>
              <Input
                type="number"
                step="0.01"
                value={settings?.pricing?.athlete_plus_direct || 0}
                onChange={(e) => updatePricing('athlete_plus_direct', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="text-primary" size={20} />
          <h2 className="font-heading text-xl uppercase">Feature Toggles</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
            <div>
              <p className="font-medium">Athlete-Led Onboarding</p>
              <p className="text-sm text-muted-foreground">
                Allow athletes to sign up independently without a coach
              </p>
            </div>
            <button onClick={() => toggleFeature('athlete_led_onboarding')}>
              {settings?.features?.athlete_led_onboarding ? (
                <ToggleRight className="text-green-400" size={32} />
              ) : (
                <ToggleLeft className="text-muted-foreground" size={32} />
              )}
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
            <div>
              <p className="font-medium">Athlete Pass Plus</p>
              <p className="text-sm text-muted-foreground">
                Enable data ownership and coach transition features
              </p>
            </div>
            <button onClick={() => toggleFeature('athlete_pass_plus')}>
              {settings?.features?.athlete_pass_plus ? (
                <ToggleRight className="text-green-400" size={32} />
              ) : (
                <ToggleLeft className="text-muted-foreground" size={32} />
              )}
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
            <div>
              <p className="font-medium">Coach Advantage Program</p>
              <p className="text-sm text-muted-foreground">
                Free subscription for coaches with 10+ athletes
              </p>
            </div>
            <button onClick={() => toggleFeature('coach_advantage')}>
              {settings?.features?.coach_advantage ? (
                <ToggleRight className="text-green-400" size={32} />
              ) : (
                <ToggleLeft className="text-muted-foreground" size={32} />
              )}
            </button>
          </div>
        </div>

        {settings?.features?.coach_advantage && (
          <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-sm">
            <label className="text-sm text-muted-foreground">Coach Advantage Threshold (athletes)</label>
            <Input
              type="number"
              value={settings?.features?.coach_advantage_threshold || 10}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                features: {
                  ...prev.features,
                  coach_advantage_threshold: parseInt(e.target.value) || 10
                }
              }))}
              className="mt-1 max-w-32"
            />
          </div>
        )}
      </div>

      {/* Coach Levels */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <h2 className="font-heading text-xl uppercase mb-6">Coach Level Thresholds</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/30 rounded-sm">
            <p className="text-sm text-muted-foreground uppercase mb-2">Starter</p>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                value={settings?.coach_levels?.starter?.min_athletes || 0}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  coach_levels: {
                    ...prev.coach_levels,
                    starter: { ...prev.coach_levels?.starter, min_athletes: parseInt(e.target.value) }
                  }
                }))}
                className="w-20"
              />
              <span>to</span>
              <Input
                type="number"
                value={settings?.coach_levels?.starter?.max_athletes || 4}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  coach_levels: {
                    ...prev.coach_levels,
                    starter: { ...prev.coach_levels?.starter, max_athletes: parseInt(e.target.value) }
                  }
                }))}
                className="w-20"
              />
              <span>athletes</span>
            </div>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm">
            <p className="text-sm text-muted-foreground uppercase mb-2">Growth</p>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                value={settings?.coach_levels?.growth?.min_athletes || 5}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  coach_levels: {
                    ...prev.coach_levels,
                    growth: { ...prev.coach_levels?.growth, min_athletes: parseInt(e.target.value) }
                  }
                }))}
                className="w-20"
              />
              <span>to</span>
              <Input
                type="number"
                value={settings?.coach_levels?.growth?.max_athletes || 9}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  coach_levels: {
                    ...prev.coach_levels,
                    growth: { ...prev.coach_levels?.growth, max_athletes: parseInt(e.target.value) }
                  }
                }))}
                className="w-20"
              />
              <span>athletes</span>
            </div>
          </div>
          <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm">
            <p className="text-sm text-primary uppercase mb-2">Elite</p>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                value={settings?.coach_levels?.elite?.min_athletes || 10}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  coach_levels: {
                    ...prev.coach_levels,
                    elite: { ...prev.coach_levels?.elite, min_athletes: parseInt(e.target.value) }
                  }
                }))}
                className="w-20"
              />
              <span>+ athletes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminSettings;
